import { useUser } from '@auth0/nextjs-auth0'
import { AviosAuthProviderProps } from '@channel/shared-header-footer/auth-client'
import { AuthPathOptions, createAuthPathname } from '@reward-platform/auth0-b2c'

type Auth0UserContextValue = ReturnType<typeof useUser>

export type AviosAuthUserSession = NonNullable<AviosAuthProviderProps['initialUser']>

type AnyAuthUser = Auth0UserContextValue['user'] | AviosAuthUserSession | null | undefined

const isAviosAuthUserSession = (user: unknown): user is AviosAuthUserSession =>
  typeof user === 'object' && user !== null && 'tokenContent' in user

export const getAuthUserDisplayName = (user: AnyAuthUser): string => {
  if (!user) {
    return ''
  }
  if (isAviosAuthUserSession(user)) {
    return user.tokenContent.name
  }
  return user.name ?? ''
}

export const getAuthUserSub = (user: AnyAuthUser): string | undefined => {
  if (!user) {
    return undefined
  }
  if (isAviosAuthUserSession(user)) {
    return user.tokenContent.sub
  }
  return user.sub
}

export const getAuthUserEmail = (user: AnyAuthUser): string | undefined => {
  if (!user) {
    return undefined
  }
  if (isAviosAuthUserSession(user)) {
    return user.tokenContent.email
  }
  return user.email
}

type AuthHandler = (options?: Pick<AuthPathOptions, 'returnToPath'>) => void

export type AuthContextValue = Omit<Auth0UserContextValue, 'user'> & {
  user: Auth0UserContextValue['user'] | AviosAuthUserSession | null
  userId: string | null
  isAuthenticated: boolean
  handleLogin: AuthHandler
  handleLogout: AuthHandler
  getAuthHref: typeof createAuthPathname
}

export type AuthProviderProps = {
  isLoading?: boolean
  aviosAuthEnabled: boolean
  initialUser?: AviosAuthUserSession | null
  platformTheme?: string
}
